import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertCircle, AlertTriangle, Eye } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { Skeleton } from '@/shared/components/ui/skeleton';
import { cn } from '@/shared/lib/cn';
import { useActiveCountry } from '@/shared/countries/ActiveCountryProvider';
import type { EfFilters } from './EstadoFinancieroReport';
import { tituloColumna, formatMoney, EfAvisos } from './EstadoFinancieroReport';
import {
  getFlujoDetalle,
  MOSTRAR_CAUCION,
  type Alerta,
  type EvolucionAgrupar,
  type FlujoMesRow,
} from './api';

/**
 * Flujo por mes — una fila por período con los totales y los ratios que el
 * modelo de L&P publica.
 *
 * Se alimenta del mismo request que el Estado Financiero (el backend arma
 * `flujoPorMes` sobre el reporte ya calculado), así que las dos solapas no
 * pueden mostrar números distintos por más que se filtre.
 */
export function FlujoPorMesPanel({ state }: { state: EfFilters }) {
  const { query, periodo } = state;
  const data = query.data;
  const [detalleMes, setDetalleMes] = useState<string | null>(null);
  const [alertasMes, setAlertasMes] = useState<string | null>(null);

  if (query.isError) {
    return (
      <div className="flex min-h-0 flex-1 items-center justify-center rounded-2xl border bg-card">
        <div className="flex items-center gap-2 text-sm text-destructive">
          <AlertCircle className="h-4 w-4" aria-hidden />
          No se pudo calcular el flujo por mes.
        </div>
      </div>
    );
  }
  if (query.isPending) {
    return (
      <div className="min-h-0 flex-1 space-y-2 rounded-2xl border bg-card p-4">
        {Array.from({ length: 10 }).map((_, i) => (
          <Skeleton key={i} className="h-6 w-full" />
        ))}
      </div>
    );
  }
  if (!data || data.flujoPorMes.rows.length === 0) {
    return (
      <div className="flex min-h-0 flex-1 items-center justify-center rounded-2xl border bg-card text-sm text-muted-foreground">
        Sin movimientos en el período seleccionado.
      </div>
    );
  }

  const { rows, total } = data.flujoPorMes;
  const hayCaucion = MOSTRAR_CAUCION && data.caucion !== null;

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3">
      <EfAvisos data={data} />

      <div className="min-h-0 flex-1 overflow-auto rounded-2xl border bg-card">
      <table className="w-full border-separate border-spacing-0 text-sm">
        <thead className="sticky top-0 z-10">
          <tr className="[&>th]:whitespace-nowrap [&>th]:border-b [&>th]:bg-card [&>th]:px-4 [&>th]:py-3 [&>th]:text-xs [&>th]:font-medium [&>th]:uppercase [&>th]:tracking-wide [&>th]:text-muted-foreground">
            <th className="sticky left-0 z-20 text-left">
              {periodo.granularidad === 'diario' ? 'Fecha' : 'Mes-Año'}
            </th>
            <th className="text-right">Ingresos</th>
            <th className="text-right">Egresos</th>
            <th className="text-right">G/P operativo</th>
            <th className="text-right" title="Comisiones por Ventas ÷ Cobranzas por Ventas">
              Comisiones / Ventas
            </th>
            <th className="text-right" title="Total COSTOS DIRECTOS ÷ FLUJO OPERATIVO GENERADO">
              Costo dir. / Ingresos
            </th>
            <th className="text-right" title="Total COSTOS INDIRECTOS ÷ FLUJO OPERATIVO GENERADO">
              Costo ind. / Ingresos
            </th>
            <th className="text-right" title="Total de egresos operativos ÷ FLUJO OPERATIVO GENERADO">
              Costo total / Ingresos
            </th>
            {hayCaucion && (
              <th
                className="text-right text-red-700"
                title={`${data.caucion?.pct}% del G/P acumulado a la tasa diaria de la serie`}
              >
                {`Caución (${data.caucion?.pct}% cash, ${data.moneda})`}
              </th>
            )}
            <th className="w-24 text-right">Acciones</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <Fila
              key={r.mes}
              row={r}
              etiqueta={tituloColumna(r.mes, data.granularidad)}
              moneda={data.moneda}
              hayCaucion={hayCaucion}
              onOpen={() => setDetalleMes(r.mes)}
              alertas={data.alertas[r.mes]?.length ?? 0}
              onAlertas={() => setAlertasMes(r.mes)}
            />
          ))}
          <Fila
            row={total}
            etiqueta="Total"
            moneda={data.moneda}
            hayCaucion={hayCaucion}
            esTotal
          />
          {/* Proyectados: mismo formato, en itálica y sobre fondo tenue para
              que no se confundan con lo real. */}
          {data.flujoPorMes.proyectadas.map((r) => (
            <Fila
              key={r.mes}
              row={r}
              etiqueta={`Proy. ${r.mes}`}
              moneda={data.moneda}
              hayCaucion={hayCaucion}
              esProyeccion
            />
          ))}
        </tbody>
      </table>
      </div>

      <AlertasDialog
        mes={alertasMes}
        alertas={alertasMes ? (data.alertas[alertasMes] ?? []) : []}
        umbralPct={data.alertaDesvioPct}
        onClose={() => setAlertasMes(null)}
      />

      <FlujoDetalleDialog
        mes={detalleMes}
        etiqueta={detalleMes ? tituloColumna(detalleMes, data.granularidad) : ''}
        vista={data.vista}
        casa={data.casa}
        onClose={() => setDetalleMes(null)}
      />
    </div>
  );
}

function Fila({
  row,
  etiqueta,
  moneda,
  hayCaucion,
  esTotal,
  esProyeccion,
  onOpen,
  alertas = 0,
  onAlertas,
}: {
  row: FlujoMesRow;
  etiqueta: string;
  moneda: string;
  hayCaucion: boolean;
  esTotal?: boolean;
  esProyeccion?: boolean;
  onOpen?: () => void;
  alertas?: number;
  onAlertas?: () => void;
}) {
  return (
    <tr
      className={cn(
        'border-b',
        esTotal && 'bg-muted/40 font-semibold',
        esProyeccion && 'bg-sky-50/60 italic text-muted-foreground',
        !esTotal && !esProyeccion && 'hover:bg-primary/5',
      )}
    >
      <th
        scope="row"
        className={cn(
          'sticky left-0 z-10 whitespace-nowrap border-b px-4 py-2.5 text-left font-normal',
          esTotal ? 'bg-muted/60 font-semibold' : esProyeccion ? 'bg-sky-50' : 'bg-card',
        )}
      >
        {etiqueta}
      </th>
      <Monto valor={row.ingresos} moneda={moneda} tono="positivo" />
      <Monto valor={-row.egresos} moneda={moneda} tono="negativo" />
      <Monto valor={row.gpOperativo} moneda={moneda} />
      <Pct valor={row.comisionesSobreVentas} />
      <Pct valor={row.costoDirectoSobreIngresos} />
      <Pct valor={row.costoIndirectoSobreIngresos} />
      <Pct valor={row.costoTotalSobreIngresos} />
      {hayCaucion && <Monto valor={row.caucion} moneda={moneda} tono="positivo" />}
      <td className="whitespace-nowrap border-b px-4 py-2.5 text-right">
        {alertas > 0 && onAlertas && (
          <button
            type="button"
            onClick={onAlertas}
            title={`${alertas} alerta${alertas === 1 ? '' : 's'} en el período`}
            aria-label={`Ver alertas de ${etiqueta}`}
            className="mr-1 inline-flex h-8 w-8 items-center justify-center rounded-md border border-amber-300 bg-amber-50 text-amber-700 transition-colors hover:bg-amber-100"
          >
            <AlertTriangle className="h-4 w-4" aria-hidden />
          </button>
        )}
        {onOpen && (
          <button
            type="button"
            onClick={onOpen}
            title="Ver detalle del período"
            aria-label={`Ver detalle de ${etiqueta}`}
            className="inline-flex h-8 w-8 items-center justify-center rounded-md border text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
          >
            <Eye className="h-4 w-4" aria-hidden />
          </button>
        )}
      </td>
    </tr>
  );
}

function Monto({
  valor,
  moneda,
  tono,
}: {
  valor: number;
  moneda: string;
  tono?: 'positivo' | 'negativo';
}) {
  const color =
    tono === 'positivo'
      ? 'text-emerald-700'
      : tono === 'negativo'
        ? 'text-red-600'
        : valor < 0
          ? 'text-red-600'
          : 'text-emerald-700';
  return (
    <td className="whitespace-nowrap border-b px-4 py-2.5 text-right tabular-nums">
      {valor === 0 ? (
        <span className="text-muted-foreground">—</span>
      ) : (
        <span className={color}>{formatMoney(valor, moneda)}</span>
      )}
    </td>
  );
}

/** `null` = sin denominador. Se muestra vacío, nunca 0% (que significaría otra cosa). */
function Pct({ valor }: { valor: number | null }) {
  return (
    <td className="whitespace-nowrap border-b px-4 py-2.5 text-right tabular-nums">
      {valor === null ? (
        <span className="text-muted-foreground">—</span>
      ) : (
        `${(valor * 100).toFixed(1).replace('.', ',')}%`
      )}
    </td>
  );
}

/**
 * Detalle de un período. Replica el agrupamiento del modelo: Ingresos,
 * Costo directo (`costo_directo = Y`), Costo indirecto (`costo_indirecto = Y`,
 * sólo si no es directo) y Otros costos (egresos sin ninguna). Dentro de cada
 * bloque, orden alfabético.
 */
function FlujoDetalleDialog({
  mes,
  etiqueta,
  vista,
  casa,
  onClose,
}: {
  mes: string | null;
  etiqueta: string;
  vista: EfFilters['periodo']['vista'];
  casa: EfFilters['periodo']['casa'];
  onClose: () => void;
}) {
  const { config: activeCountry } = useActiveCountry();
  const [agrupar, setAgrupar] = useState<EvolucionAgrupar>('categoria');

  const q = useQuery({
    queryKey: ['flujo-detalle', activeCountry.code, mes, agrupar, vista, casa],
    queryFn: () => getFlujoDetalle({ mes: mes!, agrupar, vista, casa }),
    enabled: !!mes,
    staleTime: 60_000,
  });

  return (
    <Dialog open={!!mes} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="flex max-h-[85vh] w-[min(48rem,95vw)] max-w-none flex-col gap-0 overflow-hidden p-0">
        <DialogHeader className="shrink-0 border-b px-6 py-4">
          <DialogTitle className="pr-8">{etiqueta}</DialogTitle>
          <p className="text-sm text-muted-foreground">
            Ingresos y costos del período, agrupados por Ítem del EF —
            el mismo criterio que la fila, así que los totales cierran contra
            ella. Las devoluciones restan dentro de su bloque.
          </p>
        </DialogHeader>

        <div className="flex shrink-0 items-center gap-1 border-b px-6 py-3">
          {(
            [
              { id: 'categoria', label: 'Por categoría' },
              { id: 'cuenta', label: 'Por cuenta contable' },
            ] as const
          ).map((o) => (
            <button
              key={o.id}
              type="button"
              onClick={() => setAgrupar(o.id)}
              aria-pressed={agrupar === o.id}
              className={cn(
                'rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
                agrupar === o.id
                  ? 'bg-foreground text-background'
                  : 'text-muted-foreground hover:bg-muted',
              )}
            >
              {o.label}
            </button>
          ))}
        </div>

        {q.isPending ? (
          <div className="space-y-2 px-6 py-4">
            {Array.from({ length: 8 }).map((_, i) => (
              <Skeleton key={i} className="h-6 w-full" />
            ))}
          </div>
        ) : q.isError ? (
          <p className="px-6 py-4 text-sm text-destructive">
            No se pudo cargar el detalle.
          </p>
        ) : q.data.bloques.length === 0 ? (
          <p className="px-6 py-6 text-sm text-muted-foreground">
            Sin movimientos en el período.
          </p>
        ) : (
          <div className="min-h-0 flex-1 overflow-y-auto">
            <table className="w-full text-sm">
              <tbody>
                {q.data.bloques.map((b) => (
                  <BloqueRows key={b.id} bloque={b} moneda={q.data.moneda} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function BloqueRows({
  bloque,
  moneda,
}: {
  bloque: { id: string; label: string; rows: Array<{ label: string; monto: number }>; total: number };
  moneda: string;
}) {
  // Los ingresos en verde, todo lo demás es costo y va en rojo. El importe se
  // guarda en positivo: el encabezado del bloque ya dice de qué lado está.
  const esIngreso = bloque.id === 'ingresos';
  const color = esIngreso ? 'text-emerald-700' : 'text-red-600';
  return (
    <>
      <tr className="bg-muted/40">
        <th
          colSpan={2}
          className="border-y px-6 py-2 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground"
        >
          {bloque.label}
        </th>
      </tr>
      {bloque.rows.map((r) => (
        <tr key={r.label} className="border-b hover:bg-primary/5">
          <td className="max-w-[24rem] truncate px-6 py-2.5" title={r.label}>
            {r.label}
          </td>
          <td className={cn('whitespace-nowrap px-6 py-2.5 text-right tabular-nums', color)}>
            {formatMoney(r.monto, moneda)}
          </td>
        </tr>
      ))}
      <tr className="border-b font-semibold">
        <td className="px-6 py-2.5 text-right text-muted-foreground">Total</td>
        <td className={cn('whitespace-nowrap px-6 py-2.5 text-right tabular-nums', color)}>
          {formatMoney(bloque.total, moneda)}
        </td>
      </tr>
    </>
  );
}

/**
 * Alertas del período: desvíos contra el mes anterior y rubros que faltan.
 * El umbral sale del parámetro `Alerta Desvio Pct` (Configuración).
 */
function AlertasDialog({
  mes,
  alertas,
  umbralPct,
  onClose,
}: {
  mes: string | null;
  alertas: Alerta[];
  umbralPct: number;
  onClose: () => void;
}) {
  return (
    <Dialog open={!!mes} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="flex max-h-[85vh] w-[min(40rem,95vw)] max-w-none flex-col gap-0 overflow-hidden p-0">
        <DialogHeader className="shrink-0 border-b px-6 py-4">
          <DialogTitle className="pr-8">{`Alertas — ${mes ?? ''}`}</DialogTitle>
          <p className="text-sm text-muted-foreground">
            Variaciones de más de {(umbralPct * 100).toFixed(0)}% contra el mes
            anterior, y rubros sin registros. El umbral se cambia desde
            Configuración → Parámetros.
          </p>
        </DialogHeader>
        <ul className="min-h-0 flex-1 divide-y overflow-y-auto">
          {alertas.map((a, i) => (
            <li key={i} className="flex items-start gap-3 px-6 py-3 text-sm">
              <AlertTriangle
                className={cn(
                  'mt-0.5 h-4 w-4 shrink-0',
                  a.tipo === 'desvio' ? 'text-amber-600' : 'text-muted-foreground',
                )}
                aria-hidden
              />
              <span>
                {a.tipo === 'desvio' && a.categoria ? (
                  <>
                    <strong>{a.categoria}</strong> tiene un desvío de{' '}
                    <span
                      className={cn(
                        'font-semibold',
                        (a.desvio ?? 0) > 0 ? 'text-emerald-700' : 'text-red-600',
                      )}
                    >
                      {(a.desvio ?? 0) > 0 ? '+' : '−'}
                      {(Math.abs(a.desvio ?? 0) * 100).toFixed(1).replace('.', ',')}%
                    </span>{' '}
                    respecto del mes anterior.
                  </>
                ) : (
                  a.mensaje
                )}
              </span>
            </li>
          ))}
        </ul>
      </DialogContent>
    </Dialog>
  );
}
